import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';

import Arranger from '../base/arranger';
import { DesignToken } from '../base/core';
import { BrandLogo as BrandLogoLight } from '../base/icon/asenion-logo';
import { BrandLogo as BrandLogoDark } from '../base/icon/asenion-logo-dark';
import Link from '../base/link';
import Spacer, { spc } from '../base/spacer';
import Text from '../base/text';

interface Props {
  className?: string;
}

const PrivacyPolicyImpl: React.FC<Props> = ({ className }) => {
  const prefersDarkMode = false;

  return (
    <div className={className}>
      <div className="content-container">
        <Arranger direction="column">
          <Link href="/" rel="noopener">
            <div className="logo-container">
              {prefersDarkMode ? <BrandLogoLight /> : <BrandLogoDark />}
            </div>
          </Link>
          <div className="vertical-line" />
        </Arranger>

        <Spacer pb={spc.l} />

        <Arranger direction="column" alignItems="center">
          <Text sz="subheading-first">Privacy Policy and Terms of Use</Text>
          <Spacer pb={spc.m} />
        </Arranger>

        <Spacer pb={spc.xl} />

        <div className="policy-content">
          <Text sz="body16">
            We're Fairly AI Inc. ('we', 'our', 'us') and operate under the name
            Fairly or Fairly AI. We're committed to maintaining the accuracy,
            confidentiality, and security of your personally identifiable
            information ("Personal Information"). As part of this commitment,
            our privacy policy governs our actions as they relate to the
            collection, use and disclosure of Personal Information. Our privacy
            policy is based upon the values set by the Canadian Standards
            Association's Model Code for the Protection of Personal Information
            and Canada's Personal Information Protection and Electronic
            Documents Act.
          </Text>

          <Spacer pb={spc.m} />

          <Text sz="body16">
            This notice provides you with information regarding your rights and
            obligations and explains how, why and when we collect and process
            your personal data. We take personal data seriously. Anything
            containing personally identifiable information is kept safe and we
            have put in place appropriate technical and other security measures
            to protect it.
          </Text>

          <Spacer pb={spc.m} />

          <Text sz="body16">
            For more information, email us at{' '}
            <Link href="mailto:info@fairly.ai">info@fairly.ai</Link> or write to
            us at 290 King Street E, Kitchener, ON N2H 2V5 Canada
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="subheading-second">1. Introduction</Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            We are responsible for maintaining and protecting the Personal
            Information under our control. We have designated an individual
            responsible for compliance with our privacy policy.
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="subheading-second">2. Identifying Purposes</Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            We collect, use and disclose Personal Information to provide you
            with the product or service you have requested and to offer you
            additional products and services we believe you might be interested
            in. The purposes for which we collect Personal Information will be
            identified before or at the time we collect the information. In
            certain circumstances, the purposes for which information is
            collected may be clear, and consent may be implied, such as where
            your name, address and payment information is provided as part of
            the order process.
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="subheading-second">3. Consent</Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            Knowledge and consent are required for the collection, use or
            disclosure of Personal Information except where required or
            permitted by law. Providing us with your Personal Information is
            always your choice. However, your decision not to provide certain
            information may limit our ability to provide you with our products
            or services. We will not require you to consent to the collection,
            use, or disclosure of information as a condition to the supply of a
            product or service, except as required to be able to supply the
            product or service.
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="subheading-second">4. Limiting Collection</Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            The Personal Information collected will be limited to those details
            necessary for the purposes identified by us. With your consent, we
            may collect Personal Information from you in person, over the
            telephone or by corresponding with you via mail, facsimile, or the
            Internet.
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="subheading-second">
            5. Limiting Use, Disclosure and Retention
          </Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            Personal Information may only be used or disclosed for the purpose
            for which it was collected unless you have otherwise consented, or
            when it is required or permitted by law. Personal Information will
            only be retained for the period of time required to fulfill the
            purpose for which we collected it or as may be required by law.
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="subheading-second">6. Accuracy</Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            Personal Information will be maintained in as accurate, complete and
            up-to-date form as is necessary to fulfill the purposes for which it
            is to be used.
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="subheading-second">
            7. Safeguarding Customer Information
          </Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            Personal Information will be protected by security safeguards that
            are appropriate to the sensitivity level of the information. We take
            all reasonable precautions to protect your Personal Information from
            any loss or unauthorized use, access or disclosure.
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="subheading-second">8. Openness</Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            We will make information available to you about our policies and
            practices with respect to the management of your Personal
            Information.
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="subheading-second">9. Customer Access</Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            Upon request, you will be informed of the existence, use and
            disclosure of your Personal Information, and will be given access to
            it. You may verify the accuracy and completeness of your Personal
            Information, and may request that it be amended, if appropriate.
            However, in certain circumstances permitted by law, we will not
            disclose certain information to you. For example, we may not
            disclose information relating to you if other individuals are
            referenced or if there are legal, security or commercial proprietary
            restrictions.
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="subheading-second">
            10. Handling Customer Complaints and Suggestions
          </Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            You may direct any questions or enquiries with respect to our
            privacy policy or our practices by contacting:{' '}
            <Link href="mailto:info@fairly.ai">info@fairly.ai</Link>
          </Text>

          <Spacer pb={spc.xl} />

          <Text sz="bold-body16">
            Additional information for our European customers
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="bold-body16">What data do we collect?</Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            <strong>When you interact with the Fairly website:</strong>
            <br />
            We gather information on your device, location and how you navigate
            around our website. We use this information to improve our platform
            and service as it helps us identify the type of people that our
            messaging most resonates with and what parts of our site they
            understand. Your details may be shared with third party platforms
            including Google Analytics. In all cases, this data is anonymous as
            it does not include any personal information (e.g. your name).
          </Text>

          <Spacer pb={spc.m} />

          <Text sz="body16">
            <strong>When you sign up for our waiting list:</strong>
            <br />
            We collect your email address when you sign up for our waiting list.
            We use this to keep you informed and updated on our progress and
            allow you to be part of the Fairly community. When we leave your
            email address we will confirm that you have opted into our services.
            Any electronic marketing communications we send you will include
            clear instructions to follow should you wish to unsubscribe at any
            time.
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="bold-body16">Why we use your information?</Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            European Data protection laws say we need to have a lawful basis for
            using your personal data. At least one of the following must apply:
            contractual or legal duty, legitimate interest, public interest,
            vital individual interest or consent. In this section we explain
            which one we rely on to use your data in a certain way.
          </Text>

          <Spacer pb={spc.m} />

          <Text sz="body16">
            At this stage in our product, we use your data when it's in our
            'legitimate interest' and with your consent.
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="bold-body16">Legitimate interest</Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            We need to use your data for our legitimate interests, or those of a
            third party. This means using data in a way that you might expect us
            to, for a reason which is in your and/or our (or a third party's)
            interest and which doesn't involve overriding your privacy rights.
          </Text>

          <Spacer pb={spc.m} />

          <Text sz="body16">
            <strong>For marketing we:</strong>
          </Text>
          <Spacer pb={spc.xs} />
          <Text sz="body16">
            Tell you about products and services through our website or other
            channels such as social media based on how you engage with our site
            and overtime how you engage with our product. We do this so we can
            make sure our marketing is useful. That includes instructing
            platforms to show or not show Fairly adverts to existing customers.
            We don't share any identifying information with social media
            companies other than your mobile advertising ID. You can disable or
            reset your mobile advertising ID in your device's operating system.
          </Text>

          <Spacer pb={spc.m} />

          <Text sz="body16">
            Track, analyse and improve the services we give you and other
            customers and how you respond to ads we show. We may ask for
            feedback if you've shown interest in a service. We do this so that
            we can make our products better and understand how to market them.
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="bold-body16">Consent</Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            We'll ask for your consent before we send you any marketing mail
            telling you about our product and service. Any electronic marketing
            communications we send you will include clear instructions to follow
            should you wish to unsubscribe at any time. We do not rely on
            consent as a legal basis for processing your personal data where we
            use the data to meet our contractual obligations to you and / or
            regulatory requirements.
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="bold-body16">Where we store or send your data</Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            We may transfer and store the data we collect from you to
            organisations outside the European Economic Area ('EEA'). When we do
            this, we make sure that your data is protected and that: the
            European Commission says the country or organisation has adequate
            data protection, or we've agreed to standard data protection clauses
            approved by the European Commission with the organisation.
          </Text>

          <Spacer pb={spc.l} />

          <Text sz="bold-body16">What third parties do we currently use?</Text>
          <Spacer pb={spc.s} />
          <ul>
            <li>
              <Text sz="body16">Google analytics</Text>
            </li>
            <li>
              <Text sz="body16">Webflow</Text>
            </li>
            <li>
              <Text sz="body16">Wix</Text>
            </li>
            <li>
              <Text sz="body16">Mailchimp</Text>
            </li>
            <li>
              <Text sz="body16">Hubspot</Text>
            </li>
          </ul>

          <Spacer pb={spc.m} />

          <Text sz="body16">
            We may make changes to this privacy policy, so please review it
            every now and then. If appropriate we will notify you by email about
            changes. Remember, you will be bound by this notice.
          </Text>

          <Spacer pb={spc.m} />

          <Text sz="body16">Version 1.0.0</Text>

          <Spacer pb={spc.xl} />

          <Text sz="bold-body16">Terms of use</Text>
          <Spacer pb={spc.s} />
          <Text sz="body16">
            These terms apply to all users of the Fairly app and website and by
            using them you agree you have read and accepted terms below and the{' '}
            <Link
              href="https://cdn.prod.website-files.com/65a576499a1f3aa9462e90fb/65fef6cadc24ec97fb1fa2c0_Fairly%20AI%20Inc%20-%20Terms%20and%20Conditions.pdf"
              target="_blank"
            >
              Fairly AI Terms and Conditions
            </Link>
            .
          </Text>

          <Spacer pb={spc.m} />

          <Text sz="body16">
            The Fairly app and website are made available for use in connection
            with your use of the Fairly service. You may retrieve and display
            Fairly URL links and pages on a computer or mobile screen and may
            print individual pages and store pages for your personal,
            non-commercial use. Any other use of Fairly materials such as
            copying URL links or content onto another website or use of the
            pages or app for anything other than your personal, non-commercial
            use is prohibited without our prior written permission. The
            intellectual property in all design, graphics, text and other
            material and its arrangement in Fairly is owned by us and our
            respective licensors.
          </Text>

          <Spacer pb={spc.m} />

          <Text sz="body16">
            Fairly provides links to other websites beyond our control. The
            links to other websites are there for your convenience and we are
            not responsible for and do not give any warranties or make any
            representations regarding such websites. We are not responsible for
            or liable in relation to the content or security of these sites
            (other than to the extent required by law.)
          </Text>

          <Spacer pb={spc.m} />

          <Text sz="body16">
            Fairly makes reasonable endeavours to ensure reliability of the
            information presented on the app and website, but makes no
            warranties or representations whatsoever as to its accuracy or
            reliability. Similarly, due to the nature of the internet, we do not
            guarantee that the Fairly will be available on an uninterrupted and
            error free basis.
          </Text>
        </div>

        <Spacer pb={spc.xxl} />
        <div className="vertical-line" />
        <Spacer pb={spc.xs} />

        <div className="footer">
          <Link href="/" rel="noopener">
            <div className="logo-container">
              {prefersDarkMode ? <BrandLogoLight /> : <BrandLogoDark />}
            </div>
          </Link>
          <Text>© Copyright Fairly AI</Text>
          <Text>
            <Link
              href="https://cdn.prod.website-files.com/65a576499a1f3aa9462e90fb/65fef6cadc24ec97fb1fa2c0_Fairly%20AI%20Inc%20-%20Terms%20and%20Conditions.pdf"
              target="_blank"
              style={{ color: DesignToken.link }}
            >
              Terms of Use
            </Link>
          </Text>
        </div>
      </div>
    </div>
  );
};

export const PrivacyPolicy = muiStyled(PrivacyPolicyImpl)(
  ({ theme }) => css`
    .content-container {
      padding: 20px 170px;
      max-width: 1200px;
      margin: 0 auto;
      @media (max-width: ${theme.breakpoints.values.sm}px) {
        padding: 20px 20px;
      }
      @media (max-width: ${theme.breakpoints.values.md}px) {
        padding: 20px 40px;
      }
    }

    .logo-container {
      margin-right: ${spc.l * 2}px;

      svg {
        width: 150px;
        height: 50px;
      }
    }

    .policy-content {
      max-width: 800px;
      margin: 0 auto;
      line-height: 1.6;

      ul {
        margin-left: 20px;
        margin-bottom: ${spc.s};

        li {
          margin-bottom: ${spc.xs};
        }
      }
    }

    .footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      @media (max-width: ${theme.breakpoints.values.md}px) {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: ${spc.s};
      }
    }

    .vertical-line {
      height: 0.1px;
      width: auto;
      background-color: gray;
    }
  `
);

export default PrivacyPolicy;
